import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { Icon } from '../../utils/icons';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_CFG = {
  buyer_applied:      { icon: 'user',         color: '#2563EB', bg: '#eff6ff' },
  // 🔑 broker gaps (2026-11-02): an LO's borrower registered from the file they sent (backend LO_BORROWER_REGISTERED_ALERT).
  borrower_registered:{ icon: 'user',         color: '#2563EB', bg: '#eff6ff' },
  buyer_approved:     { icon: 'check-circle', color: '#166534', bg: '#f0fdf4' },
  home_shopping:      { icon: 'home',         color: '#7c3aed', bg: '#f5f3ff' },
  offer_accepted:     { icon: 'key',          color: '#0891b2', bg: '#ecfeff' },
  closing_scheduled:  { icon: 'calendar',     color: '#3a6ad9', bg: '#eff6ff' },
  loan_closed:        { icon: 'check-circle', color: '#acf44e', bg: '#f9ffe6' },
  invite_not_accepted:{ icon: 'clock',        color: '#f97316', bg: '#fff7ed' },
};

const timeAgo = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), dy = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (dy === 1) return 'Yesterday';
  if (dy < 7) return `${dy}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export default function DoorlyProAlerts() {
  const { token } = useDoorlyProAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchAlerts = useCallback(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/alerts`, { headers: authHeaders })
      .then(res => setAlerts(res.data?.alerts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, read: 1 } : a));
    await axios.patch(`${API_URL}/api/doorlypro/alerts/${id}/read`, {}, { headers: authHeaders }).catch(() => {});
  };

  const markAllRead = async () => {
    setAlerts(prev => prev.map(a => ({ ...a, read: 1 })));
    await axios.patch(`${API_URL}/api/doorlypro/alerts/read-all`, {}, { headers: authHeaders }).catch(() => {});
  };

  const dismiss = async (id, e) => {
    e.stopPropagation();
    setAlerts(prev => prev.filter(a => a.id !== id));
    await axios.delete(`${API_URL}/api/doorlypro/alerts/${id}`, { headers: authHeaders }).catch(() => {});
  };

  const unread = alerts.filter(a => !a.read).length;
  const filtered = filter === 'unread' ? alerts.filter(a => !a.read) : alerts;

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 720, margin: '0 auto', boxSizing: 'border-box' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' }}>My Alerts</h1>
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                {unread > 0 ? `${unread} unread` : 'All caught up'} · {alerts.length} total
              </p>
            </div>
            {unread > 0 && (
              <button onClick={markAllRead}
                style={{ padding: '7px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Icon name="check" size={13} color="#374151" /> Mark all read
              </button>
            )}
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {[{ value: 'all', label: `All (${alerts.length})` }, { value: 'unread', label: `Unread (${unread})` }].map(tab => (
              <button key={tab.value} onClick={() => setFilter(tab.value)}
                style={{ padding: '7px 16px', background: filter === tab.value ? '#fff' : 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: filter === tab.value ? 700 : 500, color: filter === tab.value ? '#111827' : '#6b7280', boxShadow: filter === tab.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
              <Icon name="bell" size={36} color="#d1d5db" style={{ marginBottom: 12 }} />
              <div>Loading alerts...</div>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 60, textAlign: 'center' }}>
              <div style={{ marginBottom: 14 }}><Icon name="bell" size={40} color="#d1d5db" /></div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                {filter === 'unread' ? 'No unread alerts' : 'No alerts yet'}
              </div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                {filter === 'unread' ? "You're all caught up!" : 'Alerts appear here when there are updates about your buyers'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(alert => {
                const cfg = TYPE_CFG[alert.type] || { icon: 'bell', color: '#6b7280', bg: '#f3f4f6' };
                const isRead = !!alert.read;
                return (
                  <div key={alert.id} onClick={() => { if (!isRead) markRead(alert.id); }}
                    style={{
                      background: isRead ? '#fff' : '#fafcff',
                      border: `1px solid ${isRead ? '#e5e7eb' : '#bfdbfe'}`,
                      borderLeft: `4px solid ${isRead ? '#e5e7eb' : cfg.color}`,
                      borderRadius: 12, padding: '14px 16px',
                      display: 'flex', alignItems: 'flex-start', gap: 14,
                      cursor: isRead ? 'default' : 'pointer', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isRead) e.currentTarget.style.background = '#f0f4ff'; }}
                    onMouseLeave={e => e.currentTarget.style.background = isRead ? '#fff' : '#fafcff'}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={cfg.icon} size={18} color={cfg.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                        <div style={{ fontSize: 14, fontWeight: isRead ? 600 : 700, color: '#111827', lineHeight: 1.4 }}>{alert.title}</div>
                        {!isRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3a6ad9', flexShrink: 0, marginTop: 4 }} />}
                      </div>
                      {alert.body && <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: 6 }}>{alert.body}</div>}
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{timeAgo(alert.created_at)}</div>
                    </div>
                    <button onClick={(e) => dismiss(alert.id, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0 }} title="Dismiss">×</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
